import { useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import { formatDateForInput } from '../../utils/formatDateForInput';
import { Building2, MapPin, Calendar, AlertCircle, Edit, Wrench, RotateCcw, User, Phone, Mail } from 'lucide-react';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import BuildingMandatesSection from '../../components/BuildingMandatesSection';
import BuildingExternalPartnersSection from '../../components/BuildingExternalPartnersSection';
import BwofWorkflowPanel from '../../components/BwofWorkflowPanel';
import { InfoButton } from '../../components/InfoModal';
import { WorkerMarkersInfoButton } from '../../components/BuildingWorkerInfo';
import toast from 'react-hot-toast';
import { compareSsCode } from '../../utils/ssCodeSort';
import {
  filterInstancesForInspectionScope,
  isPartialInspectionScope,
} from '../../utils/inspectionScope';

function BuildingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: buildingRaw, isLoading, isError, error } = useQuery({
    queryKey: ['building', id],
    queryFn: async () => {
      const response = await api.get(`/buildings/${id}`);
      return response.data;
    },
  });
  const building =
    buildingRaw && typeof buildingRaw === 'object' && !Array.isArray(buildingRaw) ? buildingRaw : null;

  const { data: csCopies = [] } = useQuery({
    queryKey: ['building', id, 'compliance-schedules'],
    queryFn: async () => {
      const response = await api.get(`/buildings/${id}/compliance-schedules`);
      return response.data?.data || [];
    },
    enabled: !!id,
  });

  const restoreMutation = useMutation({
    mutationFn: async () => (await api.post(`/buildings/${id}/restore`)).data,
    onSuccess: (data) => {
      toast.success(`Active again: ${data.name || 'Building'}`);
      queryClient.invalidateQueries(['buildings']);
      queryClient.invalidateQueries(['building', id]);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to set building Active'),
  });

  const { data: ssProgressData } = useQuery({
    queryKey: ['building', id, 'ss-progress'],
    queryFn: async () => {
      const response = await api.get(`/buildings/${id}/ss-progress`);
      return response.data;
    },
    enabled: !!id,
  });
  const ssProgressRows = (() => {
    const rows = Array.isArray(ssProgressData?.rows) ? ssProgressData.rows : [];
    let filtered = rows;
    if (building && isPartialInspectionScope(building)) {
      const scoped = filterInstancesForInspectionScope(
        building.ssInstancesInScope ||
          building.ssInstances ||
          building.ss_instances ||
          [],
        building
      );
      const codes = new Set(scoped.map((i) => i.ss_code).filter(Boolean));
      filtered = rows.filter((r) => codes.has(r.ss_code));
    }
    return [...filtered].sort((a, b) => compareSsCode(a?.ss_code, b?.ss_code));
  })();

  const { data: mandatesRaw = [] } = useQuery({
    queryKey: ['building', id, 'mandates'],
    queryFn: async () => {
      const raw = (await api.get(`/mandates/buildings/${id}`)).data?.data;
      return Array.isArray(raw) ? raw : [];
    },
    enabled: !!id,
  });
  const mandates = Array.isArray(mandatesRaw) ? mandatesRaw : [];

  const canManageBwof =
    user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'SUPER_ADMIN';

  const canCompleteBwofCycle =
    canManageBwof &&
    (mandates.length === 0 || mandates.some((m) => m.mandate_type === 'FULL_BWOF'));

  // Schedule deep-link #ss-progress ??unified BWOF progress panel
  useEffect(() => {
    if (location.hash !== '#ss-progress') return;
    const el = document.getElementById('ss-progress');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, ssProgressRows.length]);

  // Our Partner ??building related companies
  useEffect(() => {
    if (location.hash !== '#service-mandates') return;
    const el = document.getElementById('service-mandates');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, id, mandates.length]);

  return (
    <div>
      <BuildingPageHeading
        buildingId={id}
        buildingName={building?.name}
        title={building?.name || (isLoading ? 'Loading building…' : 'Building')}
        infoTitle={building?.name || 'Building'}
        infoChildren={
          <div className="space-y-2 text-sm">
            {building?.address ? <p>{building.address}</p> : null}
            <p>
              SS progress, jobs, and document folders for this site.
              Due asks the default IQP for a date; the work pack is issued automatically.
            </p>
          </div>
        }
        actions={
          building && building.status !== 'INACTIVE' ? (
            <button
              type="button"
              onClick={() => navigate(`/buildings/${id}/edit`)}
              className="flex items-center px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 text-sm"
            >
              <Edit className="w-4 h-4 mr-1.5" />
              Edit
            </button>
          ) : null
        }
      />

      {isLoading && <div className="text-center py-12 text-gray-600">Loading…</div>}

      {isError && !building && (
        <div className="rounded-lg border border-red-200 bg-white p-6">
          <p className="text-red-800 font-semibold">Could not load this building</p>
          <p className="mt-2 text-sm text-gray-700">
            {error?.response?.data?.error || error?.message || 'The server did not return building data.'}
          </p>
        </div>
      )}

      {!isLoading && !building && !isError && (
        <div className="text-center py-12 text-gray-600">Building not found</div>
      )}

      {building ? (
        <>
      {building.status === 'INACTIVE' && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-amber-950">
            This building is <strong>Inactive</strong> (hidden from the portfolio and apps). All data is kept.
          </p>
          <button
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  `Set "${building.name}" to Active again?\n\nIt will show in the portfolio and apps.`
                )
              ) {
                restoreMutation.mutate();
              }
            }}
            disabled={restoreMutation.isLoading}
            className="inline-flex items-center px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />
            Active
          </button>
        </div>
      )}

      {/* Building Info */}
      <div className="bg-card rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Building Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start">
            <MapPin className="w-5 h-5 text-gray-400 mr-3 mt-1" />
            <div>
              <p className="text-sm font-medium text-gray-500">Address</p>
              <p className="text-gray-900">{building.address}</p>
            </div>
          </div>
          <div className="flex items-start">
            <Calendar className="w-5 h-5 text-gray-400 mr-3 mt-1" />
            <div>
              <p className="text-sm font-medium text-gray-500">CS Issue Date</p>
              <p className="text-gray-900">
                {building.compliance_schedule_anniversary
                  ? formatDateForInput(building.compliance_schedule_anniversary)
                  : '—'}
              </p>
            </div>
          </div>
          {building.initial_inspection_status && (
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-gray-400 mr-3 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-500">Initial Inspection Status</p>
                <p className="text-gray-900">{building.initial_inspection_status}</p>
              </div>
            </div>
          )}
          <div className="flex items-start">
            <Building2 className="w-5 h-5 text-gray-400 mr-3 mt-1" />
            <div>
              <p className="text-sm font-medium text-gray-500">Compliance Schedule</p>
              {csCopies.length > 0 ? (
                <Link
                  to={`/buildings/${id}/compliance-schedule`}
                  className="text-primary-700 hover:underline font-medium"
                >
                  {building.compliance_schedule_number || 'View copy'}
                </Link>
              ) : (
                <p className="text-gray-900">{building.compliance_schedule_number || '—'}</p>
              )}
              {csCopies.length > 0 ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  {csCopies.length === 1 ? '1 copy' : `${csCopies.length} copies`}
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex items-start">
            <Wrench className="w-5 h-5 text-gray-400 mr-3 mt-1" />
            <div>
              <p className="text-sm font-medium text-gray-500 inline-flex flex-wrap items-center gap-x-2">
                Floor plan unit marker editing
                <WorkerMarkersInfoButton className="text-gray-600" />
              </p>
              <p className={building.worker_can_move_unit_markers ? 'text-amber-700 font-medium' : 'text-gray-900'}>
                {building.worker_can_move_unit_markers ? 'On' : 'Off'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-card rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex flex-wrap items-center gap-2">
          Owner / Customer (from CS)
          <InfoButton title="Owner / Customer (from CS)">
            <p>
              Contact details for the building owner or body corporate, usually from the Compliance Schedule.
              Edit them on the Edit Building page.
            </p>
          </InfoButton>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start">
            <User className="w-5 h-5 text-gray-400 mr-3 mt-1" />
            <div>
              <p className="text-sm font-medium text-gray-500">Owner name</p>
              <p className="text-gray-900">{building.owner_name || '—'}</p>
            </div>
          </div>
          <div className="flex items-start">
            <Phone className="w-5 h-5 text-gray-400 mr-3 mt-1" />
            <div>
              <p className="text-sm font-medium text-gray-500">Phone</p>
              <p className="text-gray-900">{building.owner_telephone_work || '—'}</p>
            </div>
          </div>
          <div className="flex items-start">
            <Mail className="w-5 h-5 text-gray-400 mr-3 mt-1" />
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="text-gray-900">{building.owner_email || '—'}</p>
            </div>
          </div>
          <div className="flex items-start">
            <MapPin className="w-5 h-5 text-gray-400 mr-3 mt-1" />
            <div>
              <p className="text-sm font-medium text-gray-500">Mailing address</p>
              <p className="text-gray-900">{building.owner_mailing_address || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      <BuildingMandatesSection buildingId={id} canManage={canManageBwof} building={building} />
      <BuildingExternalPartnersSection buildingId={id} />

      <BwofWorkflowPanel
        buildingId={id}
        canManage={canManageBwof}
        canCompleteCycle={canCompleteBwofCycle}
        ssProgressRows={ssProgressRows}
      />
        </>
      ) : null}
    </div>
  );
}

export default BuildingDetail;
